// Kopie von docs/design/accent.ts (Design-System §2.3). Leitet aus der Hausfarbe eines Mandanten die Akzent-Variablen ab.
// Einzige Änderung gegenüber der Vorlage: typsichere Sortierung in contrast() (noUncheckedIndexedAccess).

export type AccentStyle = 'mono' | 'deep' | 'bright';

export interface AccentVars {
  h: number; // --accent-h
  c: number; // --accent-c
  /** Empfehlung: 'mono' ohne Buntheit, 'bright' bei heller Hausfarbe, sonst 'deep' */
  style: AccentStyle;
  /** true, wenn die Hausfarbe selbst als Vollton mit dunkler Schrift lesbar ist */
  brightAllowed: boolean;
}

type Rgb = [number, number, number];

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

function hexToLinear(hex: string): Rgb {
  const n = hex.replace('#', '');
  return [0, 2, 4].map((i) => srgbToLinear(parseInt(n.slice(i, i + 2), 16) / 255)) as Rgb;
}

function linearToOklab([r, g, b]: Rgb): Rgb {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToLinear([L, a, b]: Rgb): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const oklchToLinear = (L: number, C: number, H: number): Rgb =>
  oklabToLinear([L, C * Math.cos((H * Math.PI) / 180), C * Math.sin((H * Math.PI) / 180)]);

const inSrgb = (rgb: Rgb) => rgb.every((v) => v >= -0.0005 && v <= 1.0005);

/** größte Buntheit, die bei Helligkeit L und Farbton H noch in sRGB liegt */
function maxChroma(L: number, H: number): number {
  let lo = 0;
  let hi = 0.4;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (inSrgb(oklchToLinear(L, mid, H))) lo = mid;
    else hi = mid;
  }
  return lo;
}

function contrast(a: Rgb, b: Rgb): number {
  const lum = (c: Rgb) => 0.2126 * clamp01(c[0]) + 0.7152 * clamp01(c[1]) + 0.0722 * clamp01(c[2]);
  const hi = Math.max(lum(a), lum(b));
  const lo = Math.min(lum(a), lum(b));
  return (hi + 0.05) / (lo + 0.05);
}
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function deriveAccent(brandHex: string): AccentVars {
  const brand = hexToLinear(brandHex);
  const [L, a, b] = linearToOklab(brand);
  const C = Math.hypot(a, b);
  const H = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  if (C < 0.02) return { h: 255, c: 0.01, style: 'mono', brightAllowed: false };

  // Die Stufen 500/550/600 tragen die volle Buntheit – sie begrenzen den Wert.
  const cap = Math.min(maxChroma(0.465, H), maxChroma(0.53, H), maxChroma(0.59, H), 0.17);
  const c = Math.floor(Math.max(0.04, Math.min(C, cap)) * 1000) / 1000;
  const h = Math.round(H * 10) / 10;

  // "bright": Schrift ist accent-950; geprüft wird der dunkelste Zustand (gedrückt = 10 % Schwarz).
  const onAccent = oklchToLinear(0.22, c * 0.49, h);
  const pressed = oklabToLinear([L * 0.9, a * 0.9, b * 0.9]);
  const brightAllowed = contrast(onAccent, pressed) >= 4.5;

  return { h, c, style: brightAllowed ? 'bright' : 'deep', brightAllowed };
}

export function applyAccent(
  vars: AccentVars,
  brandHex: string,
  style: AccentStyle = vars.style,
  root: HTMLElement = document.documentElement,
): void {
  root.style.setProperty('--accent-h', String(vars.h));
  root.style.setProperty('--accent-c', String(vars.c));
  root.style.setProperty('--tenant-brand', brandHex);
  const effective = style === 'bright' && !vars.brightAllowed ? 'deep' : style; // nie unlesbar
  if (effective === 'mono') delete root.dataset.accent;
  else root.dataset.accent = effective;
}
